'use client';

import {useForwardingRef} from '@rocket-ui/react-tools';
import {noop} from '@rocket-ui/tools';
import classNames from 'classnames';
import React, {forwardRef, useCallback, useEffect, useRef} from 'react';

import styles from './TextArea.module.css';

type HTMLProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'prefix'>;

export type TextAreaProps = HTMLProps & {
	value?: string;
	onChange?: (value: string) => void;
	className?: string;
	placeholder: string;
	prefix?: React.ReactNode;
	suffix?: React.ReactNode;
	nested?: boolean;
	noHoverBackground?: boolean;
	noFocusBackground?: boolean;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
	{
		value,
		placeholder,
		prefix,
		suffix,
		className,
		onChange = noop,
		nested,
		noFocusBackground,
		noHoverBackground,
		...rest
	},
	ref,
) {
	const textAreaRef = useForwardingRef<HTMLTextAreaElement>(ref);
	const prefixIconRef = useRef<HTMLDivElement>(null);

	const changeWidth = useCallback((reference: React.RefObject<HTMLTextAreaElement>) => {
		if (!reference.current) return;

		reference.current.style.height = 'auto';
		reference.current.style.height = reference.current.scrollHeight + 'px';
	}, []);

	useEffect(() => {
		changeWidth(textAreaRef);
	}, [changeWidth]);

	return (
		<div
			className={classNames(styles.wrapper, {
				[styles.disabled]: rest.disabled,
				[styles.wrapperPrimary]: !nested,
				'hover:bg-black/[.04]': !noHoverBackground,
				'focus-within:bg-black/[.04]': !noFocusBackground,
			})}
			onClick={(event) => {
				if (prefixIconRef.current && prefixIconRef.current.contains(event.target as Node)) {
					return;
				}
				textAreaRef.current?.focus();
			}}
			data-testid={'textarea'}
		>
			{prefix ? (
				<div ref={prefixIconRef} className={styles.prefix} data-testid={'textarea-prefix'}>
					{prefix}
				</div>
			) : null}
			<textarea
				{...rest}
				ref={textAreaRef}
				value={value}
				onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
					onChange(event.currentTarget.value);
					changeWidth(textAreaRef);
				}}
				className={className || styles.textarea}
				rows={1}
				placeholder={placeholder}
			/>
			{suffix}
		</div>
	);
});
